<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ isEdit ? $t('virtualClassrooms.form.editTitle') : $t('virtualClassrooms.form.createTitle') }}</h4>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4">
					<button type="button" class="btn btn-outline-secondary" @click="$router.push('/virtual-classrooms')">{{ $t('common.actions.back') }}</button>
				</div>
			</div>

			<!-- Classroom info -->
			<div class="col-lg-12 mb-6">
				<div class="card">
					<div class="card-header">
						<h5 class="mb-0">{{ $t('virtualClassrooms.form.infoSection') }}</h5>
					</div>
					<div class="card-body">
						<div class="row g-5">
							<div class="col-12">
								<div class="form-floating form-floating-outline">
									<input
										type="text"
										class="form-control"
										:class="{ 'is-invalid': errors.name }"
										:placeholder="$t('virtualClassrooms.form.namePlaceholder')"
										v-model="classroom.name"
										maxlength="200"
										autocomplete="off"
									/>
									<label>{{ $t('virtualClassrooms.form.nameLabel') }} *</label>
									<div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
								</div>
							</div>
							<div class="col-12">
								<div class="form-floating form-floating-outline">
									<textarea
										class="form-control"
										:placeholder="$t('virtualClassrooms.form.descriptionPlaceholder')"
										v-model="classroom.description"
										maxlength="500"
										style="height: 80px;"
									></textarea>
									<label>{{ $t('virtualClassrooms.form.descriptionLabel') }}</label>
								</div>
							</div>
							<div class="col-md-4">
								<div class="form-floating form-floating-outline">
									<select class="form-select" v-model="classroom.meetingProvider">
										<option value="zoom">{{ $t('virtualClassrooms.providers.zoom') }}</option>
										<option value="teams">{{ $t('virtualClassrooms.providers.teams') }}</option>
										<option value="other">{{ $t('virtualClassrooms.providers.other') }}</option>
									</select>
									<label>{{ $t('virtualClassrooms.form.providerLabel') }} *</label>
								</div>
							</div>
							<div class="col-md-8">
								<div class="form-floating form-floating-outline">
									<input
										type="url"
										class="form-control"
										:class="{ 'is-invalid': errors.meetingUrl }"
										:placeholder="$t('virtualClassrooms.form.meetingUrlPlaceholder')"
										v-model="classroom.meetingUrl"
										autocomplete="off"
									/>
									<label>{{ $t('virtualClassrooms.form.meetingUrlLabel') }} *</label>
									<div class="invalid-feedback" v-if="errors.meetingUrl">{{ errors.meetingUrl }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Days -->
			<div class="col-lg-12 mb-6">
				<div class="card">
					<div class="card-header d-flex justify-content-between align-items-center">
						<h5 class="mb-0">{{ $t('virtualClassrooms.form.daysSection') }}</h5>
						<button type="button" class="btn btn-sm btn-primary" @click="addDay">
							<i class="ri-add-line me-1"></i>{{ $t('virtualClassrooms.form.addDay') }}
						</button>
					</div>
					<div class="card-body">
						<div class="text-danger small mb-4" v-if="errors.days">{{ errors.days }}</div>

						<div v-for="(day, dIndex) in classroom.days" :key="day.key" class="row g-4 align-items-center mb-4">
							<div class="col-auto">
								<span class="badge bg-label-primary rounded-pill">{{ $t('virtualClassrooms.form.dayLabel') }} {{ dIndex + 1 }}</span>
							</div>
							<div class="col-md-4">
								<div class="form-floating form-floating-outline">
									<input type="date" class="form-control" :class="{ 'is-invalid': errors['day_' + dIndex] }" v-model="day.date" />
									<label>{{ $t('virtualClassrooms.form.dateLabel') }} *</label>
								</div>
							</div>
							<div class="col-md-3">
								<div class="form-floating form-floating-outline">
									<input type="time" class="form-control" :class="{ 'is-invalid': errors['day_' + dIndex] }" v-model="day.startTime" />
									<label>{{ $t('virtualClassrooms.form.startTimeLabel') }} *</label>
								</div>
							</div>
							<div class="col-md-3">
								<div class="form-floating form-floating-outline">
									<input type="time" class="form-control" :class="{ 'is-invalid': errors['day_' + dIndex] }" v-model="day.endTime" />
									<label>{{ $t('virtualClassrooms.form.endTimeLabel') }} *</label>
								</div>
							</div>
							<div class="col-auto">
								<button type="button" class="btn btn-sm btn-icon btn-outline-danger" @click="removeDay(dIndex)" :disabled="classroom.days.length <= 1">
									<i class="ri-close-line"></i>
								</button>
							</div>
							<div class="col-12 pt-0" v-if="errors['day_' + dIndex]">
								<div class="text-danger small">{{ errors['day_' + dIndex] }}</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Chapters -->
			<div class="col-lg-12 mb-6">
				<div class="card">
					<div class="card-header d-flex justify-content-between align-items-center">
						<h5 class="mb-0">{{ $t('virtualClassrooms.form.chaptersSection') }} ({{ classroom.chapters.length }})</h5>
						<button type="button" class="btn btn-sm btn-primary" @click="addChapter">
							<i class="ri-add-line me-1"></i>{{ $t('virtualClassrooms.form.addChapter') }}
						</button>
					</div>
					<div class="card-body">
						<div class="text-danger small mb-4" v-if="errors.chapters">{{ errors.chapters }}</div>

						<draggable
							v-model="classroom.chapters"
							item-key="key"
							handle=".chapter-drag-handle"
							animation="200"
						>
							<template #item="{ element: chapter, index: cIndex }">
								<div class="accordion mb-4">
									<div class="accordion-item border shadow-none">
										<h2 class="accordion-header d-flex align-items-center">
											<i class="ri-draggable chapter-drag-handle me-2 ms-3" style="cursor: grab; font-size: 1.2rem;"></i>
											<button
												class="accordion-button flex-grow-1"
												:class="{ collapsed: !chapter.open }"
												type="button"
												@click="chapter.open = !chapter.open"
											>
												<span class="d-flex align-items-center gap-2">
													<span class="badge bg-label-primary rounded-pill">{{ cIndex + 1 }}</span>
													<span v-if="chapter.name">{{ chapter.name }}</span>
													<span v-else class="text-muted fst-italic">{{ $t('virtualClassrooms.form.chapterNamePlaceholder') }}</span>
													<span class="badge bg-label-secondary ms-2">
														{{ $t('virtualClassrooms.form.questionCount', { count: chapter.questions.length }) }}
													</span>
												</span>
											</button>
											<button type="button" class="btn btn-sm btn-icon btn-text-danger me-3" @click.stop="removeChapter(cIndex)" :disabled="classroom.chapters.length <= 1" data-bs-toggle="tooltip" :data-bs-title="$t('virtualClassrooms.form.removeChapter')">
												<i class="ri-delete-bin-6-line"></i>
											</button>
										</h2>
										<div class="accordion-collapse" :class="{ collapse: !chapter.open, show: chapter.open }">
											<div class="accordion-body pt-5">
												<!-- Chapter name + day -->
												<div class="row g-4 mb-5">
													<div class="col-md-7">
														<div class="form-floating form-floating-outline">
															<input
																type="text"
																class="form-control"
																:class="{ 'is-invalid': errors['chapter_' + cIndex + '_name'] }"
																:placeholder="$t('virtualClassrooms.form.chapterNamePlaceholder')"
																v-model="chapter.name"
																maxlength="200"
																autocomplete="off"
															/>
															<label>{{ $t('virtualClassrooms.form.chapterNameLabel') }} *</label>
															<div class="invalid-feedback" v-if="errors['chapter_' + cIndex + '_name']">{{ errors['chapter_' + cIndex + '_name'] }}</div>
														</div>
													</div>
													<div class="col-md-5">
														<div class="form-floating form-floating-outline">
															<select class="form-select" v-model="chapter.dayId">
																<option :value="null">{{ $t('virtualClassrooms.form.noDay') }}</option>
																<option v-for="(day, dIndex) in classroom.days" :key="day.key" :value="day.dayId">
																	{{ dayOptionLabel(day, dIndex) }}
																</option>
															</select>
															<label>{{ $t('virtualClassrooms.form.chapterDayLabel') }}</label>
														</div>
													</div>
												</div>

												<!-- Questions -->
												<div class="d-flex justify-content-between align-items-center mb-3">
													<label class="fw-medium">{{ $t('microLearning.moduleForm.questionsSection') }} ({{ chapter.questions.length }}/20)</label>
													<button type="button" class="btn btn-sm btn-outline-primary" @click="addQuestion(cIndex)" :disabled="chapter.questions.length >= 20">
														<i class="ri-add-line me-1"></i>{{ $t('virtualClassrooms.form.addQuestion') }}
													</button>
												</div>
												<div class="text-danger small mb-3" v-if="errors['chapter_' + cIndex + '_questions']">{{ errors['chapter_' + cIndex + '_questions'] }}</div>

												<div v-for="(question, qIndex) in chapter.questions" :key="question.key" class="accordion mb-3">
													<div class="accordion-item border shadow-none">
														<h2 class="accordion-header d-flex align-items-center">
															<button
																class="accordion-button flex-grow-1"
																:class="{ collapsed: !question.open }"
																type="button"
																@click="question.open = !question.open"
															>
																<span class="d-flex align-items-center gap-2">
																	<span class="badge bg-label-info rounded-pill">{{ qIndex + 1 }}</span>
																	<span v-if="question.question">{{ truncate(question.question, 70) }}</span>
																	<span v-else class="text-muted fst-italic">{{ $t('virtualClassrooms.form.questionTextPlaceholder') }}</span>
																	<span class="badge bg-label-success ms-2" v-if="question.validAnswerIndices.length > 0">
																		{{ question.validAnswerIndices.length }} ✓
																	</span>
																</span>
															</button>
															<button type="button" class="btn btn-sm btn-icon btn-text-danger me-3" @click.stop="removeQuestion(cIndex, qIndex)" :disabled="chapter.questions.length <= 1" data-bs-toggle="tooltip" :data-bs-title="$t('virtualClassrooms.form.removeQuestion')">
																<i class="ri-delete-bin-6-line"></i>
															</button>
														</h2>
														<div class="accordion-collapse" :class="{ collapse: !question.open, show: question.open }">
															<div class="accordion-body pt-5">
																<!-- Question text -->
																<div class="mb-4">
																	<div class="form-floating form-floating-outline">
																		<input
																			type="text"
																			class="form-control"
																			:class="{ 'is-invalid': errors['chapter_' + cIndex + '_q_' + qIndex + '_text'] }"
																			:placeholder="$t('virtualClassrooms.form.questionTextPlaceholder')"
																			v-model="question.question"
																			autocomplete="off"
																		/>
																		<label>{{ $t('virtualClassrooms.form.questionTextPlaceholder') }}</label>
																		<div class="invalid-feedback" v-if="errors['chapter_' + cIndex + '_q_' + qIndex + '_text']">{{ errors['chapter_' + cIndex + '_q_' + qIndex + '_text'] }}</div>
																	</div>
																</div>

																<!-- Answers -->
																<div class="d-flex justify-content-between align-items-center mb-3">
																	<label class="fw-medium">{{ $t('virtualClassrooms.form.answers') }} ({{ question.answers.length }}/6)</label>
																	<button type="button" class="btn btn-sm btn-outline-primary" @click="addAnswer(cIndex, qIndex)" :disabled="question.answers.length >= 6">
																		<i class="ri-add-line me-1"></i>{{ $t('virtualClassrooms.form.addAnswer') }}
																	</button>
																</div>
																<div class="text-danger small mb-3" v-if="errors['chapter_' + cIndex + '_q_' + qIndex + '_answers']">{{ errors['chapter_' + cIndex + '_q_' + qIndex + '_answers'] }}</div>

																<div v-for="(answer, aIndex) in question.answers" :key="aIndex" class="d-flex align-items-center gap-3 mb-3">
																	<div class="form-check">
																		<input
																			class="form-check-input"
																			type="checkbox"
																			:id="'c' + chapter.key + '_q' + question.key + '_a' + aIndex"
																			:checked="question.validAnswerIndices.includes(aIndex)"
																			@change="toggleValidAnswer(cIndex, qIndex, aIndex)"
																		/>
																	</div>
																	<div class="form-floating form-floating-outline flex-grow-1">
																		<input
																			type="text"
																			class="form-control"
																			:placeholder="$t('virtualClassrooms.form.answerPlaceholder')"
																			v-model="question.answers[aIndex]"
																			autocomplete="off"
																		/>
																		<label>{{ $t('virtualClassrooms.form.answerPlaceholder') }}</label>
																	</div>
																	<button type="button" class="btn btn-sm btn-icon btn-outline-danger" @click="removeAnswer(cIndex, qIndex, aIndex)" :disabled="question.answers.length <= 2">
																		<i class="ri-close-line"></i>
																	</button>
																</div>
																<small class="text-muted d-block mb-4"><i class="ri-checkbox-circle-line me-1"></i>{{ $t('virtualClassrooms.form.correctAnswerHint') }}</small>

																<!-- Explanation -->
																<div class="form-floating form-floating-outline">
																	<textarea
																		class="form-control"
																		:placeholder="$t('virtualClassrooms.form.explanationPlaceholder')"
																		v-model="question.explanation"
																		maxlength="500"
																		style="height: 60px;"
																	></textarea>
																	<label>{{ $t('virtualClassrooms.form.explanationLabel') }}</label>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</template>
						</draggable>
					</div>
				</div>
			</div>

			<!-- Submit -->
			<div class="col-lg-12 d-flex justify-content-end gap-4 mt-4 mb-6">
				<button type="button" class="btn btn-outline-secondary" @click="$router.push('/virtual-classrooms')">{{ $t('common.actions.cancel') }}</button>
				<button type="button" class="btn btn-primary" @click="submitClassroom" :disabled="isSubmitting">
					{{ $t('common.actions.save') }}
				</button>
			</div>

		</div>
		<!-- SUCCESS MODAL -->
		<div class="modal fade" :class="{ show: showSuccess }" :style="showSuccess ? 'display: block;' : ''" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-simple modal-add-new-cc">
				<div class="modal-content">
					<div class="modal-body p-0">
						<div class="text-center">
							<CheckIcon />
							<p class="text-muted mt-5 small">
								{{ isEdit ? $t('virtualClassrooms.form.editSuccess') : $t('virtualClassrooms.form.createSuccess') }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade" :class="{ show: showSuccess }" v-if="showSuccess"></div>

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import draggable from "vuedraggable";
import CheckIcon from "@/components/CheckIcon";

export default {
	name: "VirtualClassroomForm",

	components: { draggable, CheckIcon },

	data() {
		return {
			isEdit: false,
			isSubmitting: false,
			showSuccess: false,
			classroom: {
				name: "",
				description: "",
				meetingProvider: "zoom",
				meetingUrl: "",
				days: [],
				chapters: [],
			},
			errors: {},
			keyCounter: 0,
		};
	},

	methods: {
		generateKey() {
			return ++this.keyCounter;
		},

		generateId() {
			if (typeof crypto !== "undefined" && crypto.randomUUID)
				return crypto.randomUUID();
			return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
		},

		createEmptyDay() {
			// A stable dayId is generated client-side so chapters can reference the day
			// even before the classroom is first saved (server preserves provided dayIds).
			return {
				key: this.generateKey(),
				dayId: this.generateId(),
				date: "",
				startTime: "09:00",
				endTime: "17:00",
			};
		},

		createEmptyQuestion(open = false) {
			return {
				key: this.generateKey(),
				questionId: null,
				question: "",
				questionImage: null,
				answers: ["", ""],
				validAnswerIndices: [],
				explanation: "",
				open: open,
			};
		},

		createEmptyChapter(open = false) {
			return {
				key: this.generateKey(),
				chapterId: null,
				name: "",
				dayId: null,
				questions: [this.createEmptyQuestion(true)],
				open: open,
			};
		},

		addDay() {
			this.classroom.days.push(this.createEmptyDay());
		},

		removeDay(index) {
			if (this.classroom.days.length <= 1) return;
			const removed = this.classroom.days[index];
			this.classroom.days.splice(index, 1);
			// Detach chapters that referenced the removed day
			this.classroom.chapters.forEach(chapter => {
				if (chapter.dayId === removed.dayId) chapter.dayId = null;
			});
		},

		dayOptionLabel(day, index) {
			const base = `${this.$t("virtualClassrooms.form.dayLabel")} ${index + 1}`;
			if (!day.date) return base;
			return `${base} — ${day.date.split("-").reverse().join("/")}`;
		},

		addChapter() {
			this.classroom.chapters.push(this.createEmptyChapter(true));
		},

		removeChapter(index) {
			if (this.classroom.chapters.length <= 1) return;
			this.classroom.chapters.splice(index, 1);
		},

		addQuestion(cIndex) {
			const chapter = this.classroom.chapters[cIndex];
			if (chapter.questions.length >= 20) return;
			chapter.questions.push(this.createEmptyQuestion(true));
		},

		removeQuestion(cIndex, qIndex) {
			const chapter = this.classroom.chapters[cIndex];
			if (chapter.questions.length <= 1) return;
			chapter.questions.splice(qIndex, 1);
		},

		addAnswer(cIndex, qIndex) {
			const question = this.classroom.chapters[cIndex].questions[qIndex];
			if (question.answers.length >= 6) return;
			question.answers.push("");
		},

		removeAnswer(cIndex, qIndex, aIndex) {
			const question = this.classroom.chapters[cIndex].questions[qIndex];
			if (question.answers.length <= 2) return;
			question.answers.splice(aIndex, 1);
			question.validAnswerIndices = question.validAnswerIndices
				.filter(i => i !== aIndex)
				.map(i => i > aIndex ? i - 1 : i);
		},

		toggleValidAnswer(cIndex, qIndex, aIndex) {
			const question = this.classroom.chapters[cIndex].questions[qIndex];
			const idx = question.validAnswerIndices.indexOf(aIndex);
			if (idx === -1) {
				question.validAnswerIndices.push(aIndex);
			} else {
				question.validAnswerIndices.splice(idx, 1);
			}
		},

		validateForm() {
			this.errors = {};

			if (!this.classroom.name || !this.classroom.name.trim()) {
				this.errors.name = this.$t("virtualClassrooms.form.nameRequired");
			}

			if (!this.classroom.meetingUrl || !this.classroom.meetingUrl.trim()) {
				this.errors.meetingUrl = this.$t("virtualClassrooms.form.meetingUrlRequired");
			} else if (!/^https?:\/\/.+/.test(this.classroom.meetingUrl.trim())) {
				this.errors.meetingUrl = this.$t("virtualClassrooms.form.meetingUrlInvalid");
			}

			if (this.classroom.days.length === 0) {
				this.errors.days = this.$t("virtualClassrooms.form.daysRequired");
			}

			this.classroom.days.forEach((day, i) => {
				if (!day.date || !day.startTime || !day.endTime) {
					this.errors[`day_${i}`] = this.$t("virtualClassrooms.form.dayIncomplete", { n: i + 1 });
				} else if (day.endTime <= day.startTime) {
					this.errors[`day_${i}`] = this.$t("virtualClassrooms.form.dayTimesInvalid", { n: i + 1 });
				}
			});

			if (this.classroom.chapters.length === 0) {
				this.errors.chapters = this.$t("virtualClassrooms.form.chaptersRequired");
			}

			this.classroom.chapters.forEach((chapter, c) => {
				if (!chapter.name || !chapter.name.trim()) {
					this.errors[`chapter_${c}_name`] = this.$t("virtualClassrooms.form.chapterNameRequired", { n: c + 1 });
				}

				chapter.questions.forEach((q, i) => {
					if (!q.question || !q.question.trim()) {
						this.errors[`chapter_${c}_q_${i}_text`] = this.$t("virtualClassrooms.form.questionTextRequired", { c: c + 1, n: i + 1 });
					}
					const hasEmptyAnswer = q.answers.some((a) => !a || !a.trim());
					if (hasEmptyAnswer) {
						this.errors[`chapter_${c}_q_${i}_answers`] = this.$t("virtualClassrooms.form.answerTextRequired", { c: c + 1, n: i + 1 });
					}
					if (q.validAnswerIndices.length === 0) {
						this.errors[`chapter_${c}_q_${i}_answers`] = this.$t("virtualClassrooms.form.atLeastOneCorrect", { c: c + 1, n: i + 1 });
					}
				});
			});

			// Open chapters/questions that have errors
			if (Object.keys(this.errors).length > 0) {
				this.classroom.chapters.forEach((chapter, c) => {
					let chapterHasError = !!this.errors[`chapter_${c}_name`];
					chapter.questions.forEach((q, i) => {
						if (this.errors[`chapter_${c}_q_${i}_text`] || this.errors[`chapter_${c}_q_${i}_answers`]) {
							q.open = true;
							chapterHasError = true;
						}
					});
					if (chapterHasError) chapter.open = true;
				});
			}

			return Object.keys(this.errors).length === 0;
		},

		buildPayload() {
			return {
				name: this.classroom.name.trim(),
				description: this.classroom.description ? this.classroom.description.trim() : null,
				meetingProvider: this.classroom.meetingProvider,
				meetingUrl: this.classroom.meetingUrl.trim(),
				days: this.classroom.days.map((d) => ({
					dayId: d.dayId || null,
					date: d.date,
					startTime: d.startTime,
					endTime: d.endTime,
				})),
				chapters: this.classroom.chapters.map((chapter) => ({
					chapterId: chapter.chapterId || null,
					name: chapter.name.trim(),
					dayId: chapter.dayId || null,
					questions: chapter.questions.map((q) => ({
						questionId: q.questionId || null,
						question: q.question.trim(),
						questionImage: q.questionImage || null,
						answers: q.answers.map((a) => a.trim()),
						validAnswers: q.validAnswerIndices.map((i) => q.answers[i].trim()),
						explanation: q.explanation ? q.explanation.trim() : null,
					})),
				})),
			};
		},

		async submitClassroom() {
			if (!this.validateForm()) return;
			this.isSubmitting = true;

			const payload = this.buildPayload();

			try {
				if (this.isEdit) {
					await HTTP.put(`/virtual-classrooms/${this.$route.params.id}`, payload);
				} else {
					await HTTP.post("/virtual-classrooms", payload);
				}
				this.showSuccess = true;
				setTimeout(() => {
					this.$router.push("/virtual-classrooms");
				}, 1500);
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isSubmitting = false;
			}
		},

		async fetchClassroom() {
			try {
				const response = await HTTP.get(`/virtual-classrooms/${this.$route.params.id}`);
				const classroom = response.data.classroom;

				this.classroom.name = classroom.name;
				this.classroom.description = classroom.description || "";
				this.classroom.meetingProvider = classroom.meetingProvider;
				this.classroom.meetingUrl = classroom.meetingUrl;
				this.classroom.days = classroom.days.map((d) => ({
					key: this.generateKey(),
					dayId: d.dayId,
					date: d.date,
					startTime: d.startTime,
					endTime: d.endTime,
				}));
				this.classroom.chapters = classroom.chapters.map((chapter) => ({
					key: this.generateKey(),
					chapterId: chapter.chapterId,
					name: chapter.name,
					dayId: chapter.dayId || null,
					open: false,
					questions: chapter.questions.map((q) => {
						const validIndices = q.validAnswers
							.map((va) => q.answers.indexOf(va))
							.filter((i) => i !== -1);
						return {
							key: this.generateKey(),
							questionId: q.questionId,
							question: q.question,
							questionImage: q.questionImage || null,
							answers: [...q.answers],
							validAnswerIndices: validIndices,
							explanation: q.explanation || "",
							open: false,
						};
					}),
				}));
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
				this.$router.push("/virtual-classrooms");
			}
		},

		truncate(str, maxLen) {
			if (!str) return "";
			return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
		},

		initForm() {
			this.classroom.days = [this.createEmptyDay()];
			this.classroom.chapters = [this.createEmptyChapter(true)];
		},
	},

	async mounted() {
		if (this.$route.params.id) {
			this.isEdit = true;
			await this.fetchClassroom();
		} else {
			this.initForm();
		}
	},
};
</script>

<style scoped>
.chapter-drag-handle:hover {
	color: var(--bs-primary);
}

.accordion-button:not(.collapsed) {
	background-color: rgba(var(--bs-primary-rgb), 0.05);
}

.accordion-button::after {
	margin-left: auto;
}

.accordion-button:focus {
	box-shadow: none;
}
</style>
